import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { DataService } from '../services/DataService';
import { useAuth } from './useAuth';
import { MAX_LIST_LIMIT } from '../utils/apiConstants';

const readCache = () => Promise.all([DataService.getLocus(), DataService.getAllele()]);

/**
 * Locus/allele dropdown options, read Dexie-first and refreshed from the API.
 *
 * Four components had grown their own copy of this fetcher, each with a
 * slightly different set of guards: MiceManagement checked `token` but not
 * connectivity, CageManagement checked connectivity but not `token`,
 * LitterManagement added a forceRefresh path with its own offline fallback,
 * and EmailReminderRules skipped Dexie entirely — so a settings screen opened
 * offline showed no loci at all. This is the union of the four.
 *
 * `fetchGenotypeOptions(true)` skips the cache and goes to the API, for the
 * case where the user has just created a locus or allele elsewhere; offline it
 * falls back to the cache rather than emptying the dropdowns.
 */
export function useGeneticsOptions() {
  const { token } = useAuth();
  const [locusOptions, setLocusOptions] = useState([]);
  const [alleleOptions, setAlleleOptions] = useState([]);

  const fetchGenotypeOptions = useCallback(async (forceRefresh = false) => {
    try {
      if (!forceRefresh) {
        const [locusData, alleleData] = await readCache();
        // Either list being non-empty means sync has populated Dexie. An empty
        // pair is a cold cache, not a lab with no genetics defined.
        if (locusData.length > 0 || alleleData.length > 0) {
          setLocusOptions(locusData);
          setAlleleOptions(alleleData);
          return;
        }
      }

      if (!token || !navigator.onLine) {
        if (forceRefresh) {
          const [locusData, alleleData] = await readCache();
          setLocusOptions(locusData);
          setAlleleOptions(alleleData);
        }
        return;
      }

      const [locusRes, alleleRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/locus?_limit=${MAX_LIST_LIMIT}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/allele?_limit=${MAX_LIST_LIMIT}`),
      ]);
      setLocusOptions(locusRes.data.data || []);
      setAlleleOptions(alleleRes.data.data || []);
    } catch (err) {
      console.error('Failed to fetch genotype options:', err);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchGenotypeOptions();
  }, [token, fetchGenotypeOptions]);

  return { locusOptions, alleleOptions, fetchGenotypeOptions };
}

export default useGeneticsOptions;
